// src/components/admin/EditClientAdminPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminShell } from './Sidebar';
import { LoadingState } from '../ui/CrmPage';

export default function EditClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: '', bin: '', phone: '', address: '', email: '',
    is_individual: true, is_active: true,
  });

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true); setLoadErr('');
        const r = await fetch(`${apiBase}v1/clients/${id}`);
        if (!r.ok) {
          const t = await r.text().catch(() => '');
          throw new Error(`HTTP ${r.status} ${t}`.trim());
        }
        const c = await r.json();
        if (abort) return;
        setForm({
          name: c.name ?? '',
          bin: c.bin ?? '',
          phone: c.phone ?? '',
          address: c.address ?? '',
          email: c.email ?? '',
          is_individual: !!c.is_individual,
          is_active: !!c.is_active,
        });
      } catch (e) {
        if (!abort) setLoadErr(e.message || 'Не удалось загрузить клиента');
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [apiBase, id]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === 'checkbox' ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaveErr('');
    if (!form.name.trim()) return setSaveErr('Укажите наименование');
    if (!form.bin.trim()) return setSaveErr('Укажите БИН (или 0, если не применимо)');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        bin: form.bin.trim(),
        phone: form.phone || '',
        address: form.address || '',
        email: form.email || '',
        is_individual: !!form.is_individual,
        is_active: !!form.is_active,
      };
      const r = await fetch(`${apiBase}v1/clients/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`Сохранение: HTTP ${r.status} ${t}`);
      }
      navigate(-1);
    } catch (e) {
      setSaveErr(e.message || 'Не удалось сохранить изменения');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Пометить клиента как удалённого?')) return;
    setDeleting(true);
    try {
      const r = await fetch(`${apiBase}v1/clients/${id}`, { method: 'DELETE' });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`Удаление: HTTP ${r.status} ${t}`);
      }
      navigate(-1);
    } catch (e) {
      setSaveErr(e.message || 'Не удалось удалить клиента');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminShell title="Редактировать клиента" subtitle={`ID: ${id}`}>
        <div className="crmpage crmpage--embedded">
          <div className="crmpage__main">
            <LoadingState text="Загружаем клиента…" />
          </div>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      breadcrumbs="Клиенты"
      title="Редактировать клиента"
      subtitle={`ID: ${id}`}
      actions={
        <>
          <button type="button" className="adminbtn adminbtn--danger" onClick={onDelete} disabled={deleting}>
            {deleting ? 'Удаляем…' : 'Удалить'}
          </button>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Отмена</button>
          <button type="submit" form="edit-client-form" className="adminbtn adminbtn--primary" disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!!loadErr && <div className="crmpage__alert crmpage__alert--error">{loadErr}</div>}
          {!!saveErr && <div className="crmpage__alert crmpage__alert--error">{saveErr}</div>}

          <form id="edit-client-form" className="adminform" onSubmit={onSubmit} style={{ maxWidth: 880 }}>
            <section className="adminform__card">
              <div className="adminform__section-title">Данные клиента</div>
              <div className="adminform__grid">
                <label className="adminform__field adminform__field--full">
                  <span className="adminform__label">Наименование<span className="req">*</span></span>
                  <input className="adminform__input" name="name" value={form.name} onChange={onChange} required />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">БИН<span className="req">*</span></span>
                  <input className="adminform__input" name="bin" value={form.bin} onChange={onChange} required />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Телефон</span>
                  <input className="adminform__input" name="phone" value={form.phone} onChange={onChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">E-mail</span>
                  <input type="email" className="adminform__input" name="email" value={form.email} onChange={onChange} />
                </label>
                <label className="adminform__field adminform__field--full">
                  <span className="adminform__label">Адрес</span>
                  <input className="adminform__input" name="address" value={form.address} onChange={onChange} />
                </label>
                <label className="adminform__field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingTop: 28 }}>
                  <input type="checkbox" className="crmpage__check" name="is_individual" checked={form.is_individual} onChange={onChange} />
                  <span className="adminform__label" style={{ margin: 0 }}>
                    {form.is_individual ? 'Физическое лицо' : 'Юридическое лицо'}
                  </span>
                </label>
                <label className="adminform__field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingTop: 28 }}>
                  <input type="checkbox" className="crmpage__check" name="is_active" checked={form.is_active} onChange={onChange} />
                  <span className="adminform__label" style={{ margin: 0 }}>
                    {form.is_active ? 'Клиент активен' : 'Клиент отключён'}
                  </span>
                </label>
              </div>
            </section>
          </form>
        </div>
      </div>
    </AdminShell>
  );
}
